import { useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import { Share2, Copy, Check, Download, ContactRound, Mail, MessageSquare, QrCode } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { buildVCard, downloadVCard } from "@/utils/vcard";
import { buildListingShareUrl } from "@/utils/shareUrl";

const API_V1 = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

/**
 * Public "Share This Best of Best Listing" / "Save & Share Digital Business Card" section.
 *
 * - QR code (auto-generated for the public listing URL)
 * - Native share + copy link
 * - Save contact (vCard download)
 * - SMS / email pre-filled share
 * - Tracks each share action via /horsepower/{slug}/share
 */
export default function ShareThisListingSection({ business, listingUrl }) {
  const [copied, setCopied] = useState(false);
  const qrCanvasRef = useRef(null);

  if (!business) return null;
  const isAgent = !!business.is_individual;
  // Human-facing URL — shown in the QR code + Copy Link.
  const url = listingUrl || (typeof window !== "undefined" ? window.location.href : "");
  // Backend share URL — what we send to platforms so crawlers see per-listing meta tags.
  // Humans clicking it are auto-redirected to the real listing page.
  const shareUrl = buildListingShareUrl(business) || url;
  const shareTitle = business.name;
  const shareText = isAgent
    ? `Save ${business.name}'s contact info`
    : `Check out ${business.name} on LocalBest.ai`;

  const track = (network) => {
    axios.post(`${API_V1}/horsepower/${business.slug}/share`, null, { params: { network } }).catch(() => {});
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      track("copy_link");
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      toast.error("Could not copy");
    }
  };

  const onNativeShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
        track("native");
      } else {
        onCopy();
      }
    } catch (_) { /* user cancelled */ }
  };

  const onSaveContact = () => {
    const vcard = buildVCard({
      name: business.name,
      title: isAgent ? (business.category_name || "Pro") : undefined,
      company: isAgent ? (business.company_name || "") : undefined,
      phone: business.contact_phone || business.phone_primary,
      email: business.contact_email || business.email,
      website: business.website,
      listingUrl: url,
      address: business.street_address,
      city: business.city,
      state: business.state,
      zip: business.zip_code,
      logoUrl: business.logo_url,
      category: business.category_name,
      isIndividual: isAgent,
    });
    const safeName = (business.name || "contact").replace(/[^a-z0-9]+/gi, "_");
    downloadVCard(safeName, vcard);
    track("save_contact");
    toast.success("Contact card downloaded");
  };

  const onSmsShare = () => {
    const body = encodeURIComponent(`${shareText}: ${shareUrl}`);
    window.location.href = `sms:?&body=${body}`;
    track("sms");
  };

  const onEmailShare = () => {
    const subject = encodeURIComponent(shareTitle);
    const body = encodeURIComponent(`${shareText}\n\n${shareUrl}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    track("email");
  };

  const onDownloadQrPng = () => {
    // qrcode.react v4: render an off-screen canvas, snapshot it as PNG
    const node = qrCanvasRef.current?.querySelector("canvas");
    if (!node) {
      toast.error("QR code not ready yet");
      return;
    }
    const url2 = node.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url2;
    a.download = `${(business.slug || "listing")}-qr.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    track("qr_download_png");
    toast.success("QR code PNG downloaded");
  };

  const onDownloadQrSvg = () => {
    const svgEl = document.querySelector(`[data-qr-svg="${business.slug}"] svg`);
    if (!svgEl) { toast.error("QR not ready"); return; }
    const svg = new XMLSerializer().serializeToString(svgEl);
    const blob = new Blob([svg], { type: "image/svg+xml" });
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u;
    a.download = `${(business.slug || "listing")}-qr.svg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(u), 500);
    track("qr_download_svg");
    toast.success("QR code SVG downloaded");
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45 }}
      className="rounded-2xl p-6 sm:p-8 border border-white/10 bg-gradient-to-br from-[#0F172A] via-[#0B1226] to-[#0F172A]"
      data-testid="share-this-listing-section"
    >
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#7C3AED] flex items-center justify-center flex-shrink-0">
          <Share2 className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-white font-['Outfit']">
            {isAgent ? "Save & Share This Digital Business Card" : "Share This Best of Best Listing"}
          </h2>
          <p className="text-sm text-[#94A3B8] mt-1">
            {isAgent
              ? `Scan the QR code or tap below to save ${business.name}'s contact information and share this Best of Best profile.`
              : `Know someone who needs help from ${business.name}? Scan the QR code, share this listing, or save their contact information.`}
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 items-start">
        {/* QR + downloads */}
        <div className="flex flex-col items-center md:items-start gap-3" data-qr-svg={business.slug}>
          <div ref={qrCanvasRef} className="rounded-xl bg-white p-3 shadow-lg">
            <QRCodeSVG
              value={url}
              size={172}
              level="H"
              imageSettings={business.logo_url ? {
                src: business.logo_url,
                height: 30, width: 30, excavate: true,
              } : undefined}
              data-testid="public-qr"
            />
            {/* Hidden canvas for PNG download */}
            <div className="hidden">
              <QRCodeCanvas value={url} size={512} level="H" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 justify-center md:justify-start">
            <button
              onClick={onDownloadQrPng}
              className="px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white text-xs inline-flex items-center gap-1.5 transition"
              data-testid="qr-download-png-btn"
            >
              <Download className="w-3.5 h-3.5" /> PNG
            </button>
            <button
              onClick={onDownloadQrSvg}
              className="px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white text-xs inline-flex items-center gap-1.5 transition"
              data-testid="qr-download-svg-btn"
            >
              <Download className="w-3.5 h-3.5" /> SVG
            </button>
          </div>
          <p className="text-[11px] text-[#64748B] text-center md:text-left">Scan to open this listing on any phone.</p>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <Button
            onClick={onNativeShare}
            className="w-full bg-gradient-to-r from-[#00E5FF] to-[#7C3AED] hover:opacity-90 text-white font-semibold"
            data-testid="public-share-btn"
          >
            <Share2 className="w-4 h-4 mr-2" /> Share Listing
          </Button>
          <Button
            onClick={onCopy}
            variant="outline"
            className="w-full border-white/10 bg-white/5 hover:bg-white/10 text-white"
            data-testid="public-copy-link-btn"
          >
            {copied ? <Check className="w-4 h-4 mr-2 text-[#10B981]" /> : <Copy className="w-4 h-4 mr-2" />}
            {copied ? "Copied!" : "Copy Link"}
          </Button>
          <Button
            onClick={onSaveContact}
            variant="outline"
            className="w-full border-white/10 bg-white/5 hover:bg-white/10 text-white"
            data-testid="public-save-contact-btn"
          >
            <ContactRound className="w-4 h-4 mr-2" />
            {isAgent ? "Save Contact (.vcf)" : "Save Business Contact"}
          </Button>
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={onSmsShare}
              variant="outline"
              className="border-white/10 bg-white/5 hover:bg-white/10 text-white"
              data-testid="public-sms-btn"
            >
              <MessageSquare className="w-4 h-4 mr-1" /> Text
            </Button>
            <Button
              onClick={onEmailShare}
              variant="outline"
              className="border-white/10 bg-white/5 hover:bg-white/10 text-white"
              data-testid="public-email-btn"
            >
              <Mail className="w-4 h-4 mr-1" /> Email
            </Button>
          </div>
        </div>
      </div>
    </motion.section>
  );
}
